// Canonical per-company service_type catalog.
//
// Why this lives in /shared: the set of service types a company's records
// may legitimately carry must be ONE list shared by every surface that
// writes or validates a service_type — the Mark Won override (a planning
// win choosing which case the client actually bought), the manual
// record-creation endpoints, and the service-record retype path. Before
// this module the only per-company lists were UI-side (the care-plan
// editor's PRODUCTS_BY_COMPANY) and the server accepted any string, which
// is how 8 planning wins were typed health_case while selling term life
// (2026-08-01 cutover finding).
//
// This is a VALIDATION catalog, not a display helper — labels come from
// ./service-type-labels (humanizeServiceType).

import { deriveOpportunityServiceDefaults } from "../opportunities/opportunity-service-map";

// Insurance policy kinds mirror VALID_POLICY_TYPES in
// workers/api/src/routes/insurance-policies.ts (the manual add-policy
// path builds service_type as `${kind}_policy`).
export const INSURANCE_POLICY_KINDS = [
  "home",
  "auto",
  "umbrella",
  "life",
  "health",
  "medicare",
  "disability",
  "long_term_care",
  "renters",
  "condo",
  "dwelling_fire",
  "watercraft",
  "rv",
  "motorcycle",
  "secondary_home",
  "commercial",
  "flood",
  "golf_cart",
  "other",
] as const;

// Planning case types — the real set planning_cases serves (migration
// 0211 added dental/vision/short-term; annuity_case is what a won
// planning:annuity opportunity becomes). Order is display order for
// pickers: the common health-side types first, then life/DI, then the
// rest.
export const PLANNING_SERVICE_TYPES = [
  "health_case",
  "medicare_case",
  "medishare_case",
  "life_case",
  "disability_case",
  // Long-term care (2026-09-23): case_type `long_term_care`, which the
  // planning_cases CHECK has allowed since migration 0009.
  "long_term_care_case",
  "dental_case",
  "vision_case",
  "short_term_medical_case",
  "estate_plan",
  "annuity_case",
] as const;

// Real, pickable insurance policy types — INSURANCE_POLICY_KINDS spelled
// out as service_type strings. Kept separate from
// SERVICE_TYPES_BY_COMPANY.insurance (below) because that list also
// includes the generic "insurance_policy" catch-all, which is exactly
// the ambiguous non-answer the Mark Won picker exists to replace — it
// must never appear as a choice in that picker (mirrors
// PLANNING_SERVICE_TYPES excluding planning's own generic default).
export const INSURANCE_POLICY_SERVICE_TYPES = INSURANCE_POLICY_KINDS.map(
  (k) => `${k}_policy`,
) as readonly string[];

/**
 * The generic, purpose-unknown product type each company is allowed to
 * store. These are legitimate values, NOT errors: the imports that
 * carried them genuinely did not record which product it was, and an
 * honest generic beats a wrong guess.
 *
 * They are excluded from PICKABLE_SERVICE_TYPES_BY_COMPANY below, so a
 * person starting a NEW record can never choose one. Recording "we don't
 * know" is a fact about imported history; it is never a valid answer
 * when someone is sitting there able to say which product it is.
 */
export const GENERIC_SERVICE_TYPES = new Set([
  // AC-migrated quotes whose line of business was never recorded.
  "insurance_policy",
  // Written on purpose by the processing-product-link repair pass, whose
  // own comment says why: the AC deal does not say whether the loan was
  // a purchase or a refinance. 359 of these in production 2026-09-10.
  "mortgage_loan",
]);

export const SERVICE_TYPES_BY_COMPANY: Record<string, readonly string[]> = {
  insurance: [
    ...INSURANCE_POLICY_SERVICE_TYPES,
    // Generic — see GENERIC_SERVICE_TYPES above.
    "insurance_policy",
  ],
  mortgage: [
    "purchase_mortgage",
    "refinance_mortgage",
    "heloc",
    "heloan",
    "private_money_loan",
    "reverse_mortgage",
    // Generic — see GENERIC_SERVICE_TYPES above.
    "mortgage_loan",
  ],
  financial: [
    "managed_portfolio",
    "financial_plan",
    "retirement_plan",
    "advisory_relationship",
    // Account registrations (2026-09-10). These are REAL Stewardship
    // Financial products — 468 of them in production, every one carrying
    // a portfolios row — and processing-product-link's own
    // COMPATIBLE_SERVICE_TYPES has listed them as legitimate financial
    // products all along. This catalog simply never learned them, so the
    // off-catalog detector was telling Jake and Brian Ream that every
    // real IRA in the system is filed as a type the company does not
    // have: 469 open cleanup issues, none of them actionable, because
    // there was nothing to fix.
    "ira_traditional",
    "ira_roth",
    "joint",
    "trust",
    "sep_ira",
    "simple_ira",
    "401k",
  ],
  planning: [...PLANNING_SERVICE_TYPES],
  // Rentals (2026-09-22, shared/realty/rental.ts): the landlord side
  // rides family 'listing', the tenant side family 'home_purchase'.
  realty: ["listing", "home_purchase", "rental_listing", "home_rental", "sale_transaction"],
};

/**
 * What to OFFER when a person is creating a new record, as opposed to
 * what is legal to store. The two differ only by the generics.
 *
 * Every create/retype picker should read this; validation of an existing
 * stored value reads SERVICE_TYPES_BY_COMPANY. (The retype control is
 * the one deliberate exception on the read side — it has to be able to
 * show a record's current generic type as its starting value.)
 */
export const PICKABLE_SERVICE_TYPES_BY_COMPANY: Record<string, readonly string[]> =
  Object.fromEntries(
    Object.entries(SERVICE_TYPES_BY_COMPANY).map(([slug, types]) => [
      slug,
      types.filter((t) => !GENERIC_SERVICE_TYPES.has(t)),
    ]),
  );

/** True when `serviceType` is a legitimate stored value for the company.
 *  Unknown companies validate nothing (return true) — the caller's
 *  company-resolution failure is its own error, not a type error. */
export function isValidServiceTypeForCompany(
  companySlug: string | null | undefined,
  serviceType: string,
): boolean {
  const list = companySlug ? SERVICE_TYPES_BY_COMPANY[companySlug] : undefined;
  if (!list) return true;
  return list.includes(serviceType);
}

/**
 * The case type to PRE-SELECT when a planning opportunity is marked Won.
 *
 * Returns null when the answer is genuinely ambiguous and the advisor must
 * choose:
 *   - `insurance_quote` is AC's shared type for Ryan's Health - Quoting AND
 *     Life/DI - Quoting funnels, so it cannot say which case was bought —
 *     defaulting it to health_case is exactly the bug that mistyped 8
 *     term-life/disability/Medicare sales as health cases (2026-08-01).
 *   - Any type whose map derivation lands on the generic company default
 *     (`planning_case`) carries no product signal either.
 *
 * A non-null return is a PREFILL, not a lock — the Mark Won dialog keeps
 * the picker visible so e.g. a life_di_lead that closed as disability can
 * be corrected in the same click-path.
 */
export function planningWonCaseTypePrefill(
  opportunityType: string,
): string | null {
  if (opportunityType === "insurance_quote") return null;
  const derived = deriveOpportunityServiceDefaults(
    opportunityType,
    "planning",
  ).service_type;
  return (PLANNING_SERVICE_TYPES as readonly string[]).includes(derived)
    ? derived
    : null;
}

/**
 * The policy type to PRE-SELECT when an insurance opportunity is marked
 * Won. Mirrors planningWonCaseTypePrefill above — same shape, same
 * reasoning, one company's ambiguous shared quote type instead of
 * another's.
 *
 * Returns null when the answer is genuinely ambiguous and the advisor
 * must choose: `insurance_quote` is AC's shared generic quote type (it
 * does not say home vs. auto vs. umbrella), and derivation for it lands
 * on the generic `insurance_policy` product, which carries no line-of-
 * business signal — the exact non-answer the picker exists to replace
 * (Brandon Ream, 2026-08-25: quotes accepted on the spot were landing on
 * the Processing board simply labeled "Policy Issuance").
 *
 * A non-null return is a PREFILL, not a lock — the Mark Won dialog keeps
 * the picker visible so a home_policy_quote that actually closed as a
 * bundled auto/umbrella sale can be corrected in the same click-path.
 */
export function insuranceWonPolicyTypePrefill(
  opportunityType: string,
): string | null {
  const derived = deriveOpportunityServiceDefaults(
    opportunityType,
    "insurance",
  ).service_type;
  return (INSURANCE_POLICY_SERVICE_TYPES as readonly string[]).includes(derived)
    ? derived
    : null;
}
